import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'

import { CORPORATE_KEY, corporateApi } from '@/api/corporate'
import { ApiError } from '@/api/errors'
import type { Obligation, ObligationRule } from '@/api/types'
import { RecordAnnualMeeting } from '@/components/corporate/record-annual-meeting'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useResetOnOpen } from '@/hooks/use-reset-on-open'
import { monthGrid, monthLabel, OBLIGATION_STATUS } from '@/lib/corporate'
import { formatDateOnly } from '@/lib/format'
import { localToday } from '@/lib/portal-home'
import { matching, oneOf, readSearch } from '@/lib/search-params'

export const Route = createFileRoute('/_app/corporate/calendrier')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch(
      { presentation: oneOf(['liste', 'mois'] as const), mois: matching(/^\d{4}-\d{2}$/) },
      search,
    ),
  component: CalendarPage,
})

const NONE = '__aucune__'
const WEEKDAYS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.']

/**
 * Le calendrier des obligations (lot 45) : rapport annuel, assemblée ou
 * résolution annuelle, registre de l'art. 18.1, avis à déposer, T2. Chaque
 * échéance vient d'une règle paramétrable et dit sa référence légale ; elle se
 * ferme avec sa preuve, une résolution ou une pièce.
 */
function CalendarPage() {
  const { presentation, mois } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [closing, setClosing] = useState<Obligation | null>(null)
  const [adding, setAdding] = useState(false)
  const view = presentation ?? 'liste'
  const obligations = useQuery({
    queryKey: [...CORPORATE_KEY, 'obligations'],
    queryFn: () => corporateApi.obligations(),
  })
  const today = localToday()
  const month = mois ?? today.slice(0, 7)
  const items = [...(obligations.data ?? [])].sort((a, b) => a.due_on.localeCompare(b.due_on))
  return (
    <div className="space-y-4" data-testid="calendrier">
      <Frame className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Tabs
            value={view}
            onValueChange={(next) => {
              void navigate({
                search: (prev) => ({ ...prev, presentation: next as 'liste' | 'mois' }),
                replace: true,
              })
            }}
          >
            <TabsList>
              <TabsTrigger value="liste">Liste</TabsTrigger>
              <TabsTrigger value="mois">Mois</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setAdding(true)
            }}
          >
            <PlusIcon />
            Ajouter une obligation
          </Button>
        </div>
        {obligations.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : obligations.isError ? (
          <FormError>Le calendrier n’a pas pu être lu.</FormError>
        ) : view === 'liste' ? (
          <ul className="divide-y text-sm" data-testid="calendrier-liste">
            {items.length === 0 && (
              <li className="py-2 text-muted-foreground">Aucune obligation.</li>
            )}
            {items.map((obligation) => (
              <ObligationRow key={obligation.id} obligation={obligation} onClose={setClosing} />
            ))}
          </ul>
        ) : (
          <MonthView
            month={month}
            items={items}
            onMonth={(next) => {
              void navigate({ search: (prev) => ({ ...prev, mois: next }), replace: true })
            }}
          />
        )}
      </Frame>
      <RulesSection />
      <CloseDialog
        obligation={closing}
        onOpenChange={(open) => {
          if (!open) setClosing(null)
        }}
      />
      <AddObligationDialog open={adding} onOpenChange={setAdding} />
    </div>
  )
}

function ObligationRow({
  obligation,
  onClose,
}: {
  obligation: Obligation
  onClose: (o: Obligation) => void
}) {
  const status = OBLIGATION_STATUS[obligation.status]
  const open = obligation.status !== 'done' && obligation.status !== 'dismissed'
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5" data-testid="obligation">
      <span className="w-28 shrink-0 tabular-nums">{formatDateOnly(obligation.due_on)}</span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{obligation.title}</span>
        {obligation.legal_reference && (
          <span className="block text-xs text-muted-foreground">{obligation.legal_reference}</span>
        )}
        {obligation.status === 'done' && obligation.done_on && (
          <span className="block text-xs text-muted-foreground">
            Faite le {formatDateOnly(obligation.done_on)}
          </span>
        )}
        {obligation.note && (
          <span className="block text-xs text-muted-foreground">{obligation.note}</span>
        )}
        <RecordAnnualMeeting obligation={obligation} />
      </span>
      <Badge variant={status.tone}>{status.label}</Badge>
      {open && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid="obligation-fermer"
          onClick={() => {
            onClose(obligation)
          }}
        >
          <CheckIcon />
          Marquer faite
        </Button>
      )}
    </li>
  )
}

function MonthView({
  month,
  items,
  onMonth,
}: {
  month: string
  items: Obligation[]
  onMonth: (next: string) => void
}) {
  const [year, monthNumber] = month.split('-').map(Number) as [number, number]
  const shift = (delta: number) => {
    const date = new Date(Date.UTC(year, monthNumber - 1 + delta, 1))
    onMonth(`${String(date.getUTCFullYear())}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`)
  }
  const weeks = monthGrid(year, monthNumber)
  return (
    <div className="space-y-2" data-testid="calendrier-mois">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Mois précédent"
          onClick={() => {
            shift(-1)
          }}
        >
          <ChevronLeftIcon />
        </Button>
        <p className="w-40 text-center font-medium capitalize">{monthLabel(year, monthNumber)}</p>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Mois suivant"
          onClick={() => {
            shift(1)
          }}
        >
          <ChevronRightIcon />
        </Button>
      </div>
      <table className="w-full table-fixed text-xs">
        <thead>
          <tr>
            {WEEKDAYS.map((day) => (
              <th key={day} className="py-1 font-medium text-muted-foreground">
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week.join('|')}>
              {week.map((day, index) => (
                <td key={day ?? `vide-${String(index)}`} className="h-20 border align-top">
                  {day && (
                    <div className="space-y-0.5 p-1">
                      <p className="text-muted-foreground tabular-nums">{Number(day.slice(8))}</p>
                      {items
                        .filter((o) => o.due_on === day)
                        .map((o) => (
                          <p key={o.id} className="truncate">
                            <Badge
                              variant={OBLIGATION_STATUS[o.status].tone}
                              className="max-w-full truncate"
                              title={o.title}
                            >
                              {o.title}
                            </Badge>
                          </p>
                        ))}
                    </div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CloseDialog({
  obligation,
  onOpenChange,
}: {
  obligation: Obligation | null
  onOpenChange: (open: boolean) => void
}) {
  const ids = { date: useId(), minute: useId(), document: useId(), note: useId() }
  const queryClient = useQueryClient()
  const open = obligation !== null
  const [doneOn, setDoneOn] = useState(localToday())
  const [minuteId, setMinuteId] = useState(NONE)
  const [documentId, setDocumentId] = useState(NONE)
  const [note, setNote] = useState('')
  useResetOnOpen(open, obligation?.id ?? '', () => {
    setDoneOn(localToday())
    setMinuteId(NONE)
    setDocumentId(NONE)
    setNote('')
    save.reset()
  })
  const minutes = useQuery({
    queryKey: [...CORPORATE_KEY, 'minutes'],
    queryFn: () => corporateApi.minutes(),
    enabled: open,
  })
  const documents = useQuery({
    queryKey: [...CORPORATE_KEY, 'documents'],
    queryFn: corporateApi.documents,
    enabled: open,
  })
  const save = useMutation({
    mutationFn: (status: 'done' | 'dismissed') =>
      corporateApi.updateObligation(obligation?.id ?? '', {
        status,
        ...(status === 'done' ? { done_on: doneOn } : {}),
        evidence_minute_id: minuteId === NONE ? null : minuteId,
        evidence_document_id: documentId === NONE ? null : documentId,
        note: note.trim() || null,
      }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: CORPORATE_KEY })
      onOpenChange(false)
      toast.success(
        saved.status === 'done' ? 'Obligation faite, preuve au livre.' : 'Obligation écartée.',
      )
    },
  })
  const adopted = (minutes.data ?? []).filter((m) => m.status === 'adopted')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{obligation?.title}</DialogTitle>
          <DialogDescription>
            Une obligation se ferme avec sa preuve : la résolution adoptée ou la pièce déposée.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor={ids.date}>Faite le</Label>
          <Input
            id={ids.date}
            type="date"
            className="w-44"
            value={doneOn}
            onChange={(event) => {
              setDoneOn(event.target.value)
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={ids.minute}>Résolution</Label>
          <Select value={minuteId} onValueChange={setMinuteId}>
            <SelectTrigger id={ids.minute} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Aucune</SelectItem>
              {adopted.map((minute) => (
                <SelectItem key={minute.id} value={minute.id}>
                  {minute.ref} — {minute.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor={ids.document}>Pièce</Label>
          <Select value={documentId} onValueChange={setDocumentId}>
            <SelectTrigger id={ids.document} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Aucune</SelectItem>
              {(documents.data ?? []).map((document) => (
                <SelectItem key={document.id} value={document.id}>
                  {document.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor={ids.note}>Note (exigée pour écarter)</Label>
          <Textarea
            id={ids.note}
            rows={2}
            value={note}
            onChange={(event) => {
              setNote(event.target.value)
            }}
          />
        </div>
        {save.error && (
          <FormError>
            {save.error instanceof ApiError && save.error.reason === 'note_required'
              ? 'Écarter une obligation se justifie : écrivez pourquoi.'
              : save.error instanceof ApiError
                ? save.error.message
                : 'La mise à jour n’a pas abouti.'}
          </FormError>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={save.isPending || !note.trim()}
            onClick={() => {
              save.mutate('dismissed')
            }}
          >
            Écarter
          </Button>
          <Button
            type="button"
            disabled={save.isPending || (minuteId === NONE && documentId === NONE)}
            data-testid="obligation-faite"
            onClick={() => {
              save.mutate('done')
            }}
          >
            Marquer faite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AddObligationDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const ids = { title: useId(), due: useId(), reference: useId() }
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [due, setDue] = useState('')
  const [reference, setReference] = useState('')
  useResetOnOpen(open, 'nouvelle-obligation', () => {
    setTitle('')
    setDue('')
    setReference('')
    create.reset()
  })
  const create = useMutation({
    mutationFn: () =>
      corporateApi.createObligation({
        kind: 'custom',
        title: title.trim(),
        due_on: due,
        legal_reference: reference.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CORPORATE_KEY })
      onOpenChange(false)
      toast.success('Obligation ajoutée au calendrier.')
    },
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter une obligation</DialogTitle>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor={ids.title}>Obligation</Label>
          <Input
            id={ids.title}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value)
            }}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-[11rem_1fr]">
          <div className="space-y-1">
            <Label htmlFor={ids.due}>Échéance</Label>
            <Input
              id={ids.due}
              type="date"
              value={due}
              onChange={(event) => {
                setDue(event.target.value)
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={ids.reference}>Référence légale (facultative)</Label>
            <Input
              id={ids.reference}
              value={reference}
              onChange={(event) => {
                setReference(event.target.value)
              }}
            />
          </div>
        </div>
        {create.error && (
          <FormError>
            {create.error instanceof ApiError ? create.error.message : 'L’ajout n’a pas abouti.'}
          </FormError>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Annuler
          </Button>
          <Button
            type="button"
            disabled={!title.trim() || !due || create.isPending}
            onClick={() => {
              create.mutate()
            }}
          >
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Les règles qui calculent les échéances : leur référence légale, et les activer ou non. */
function RulesSection() {
  const queryClient = useQueryClient()
  const rules = useQuery({ queryKey: [...CORPORATE_KEY, 'rules'], queryFn: corporateApi.rules })
  const save = useMutation({
    mutationFn: (next: ObligationRule[]) => corporateApi.saveRules(next),
    onSuccess: (saved) => {
      queryClient.setQueryData([...CORPORATE_KEY, 'rules'], saved)
      void queryClient.invalidateQueries({ queryKey: [...CORPORATE_KEY, 'obligations'] })
    },
  })
  return (
    <Frame className="space-y-3" data-testid="regles-obligations">
      <h2 className="font-semibold">Règles du calendrier</h2>
      <p className="text-sm text-muted-foreground">
        Les délais viennent de la loi et se règlent ici, sans attendre une nouvelle version de
        l’application. Ceux marqués « à confirmer » sont à valider par votre conseiller.
      </p>
      {rules.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : rules.isError ? (
        <FormError>Les règles n’ont pas pu être lues.</FormError>
      ) : (
        <ul className="divide-y text-sm">
          {rules.data.map((rule) => (
            <li
              key={rule.key}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
              data-testid="regle-obligation"
            >
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{rule.title}</span>
                <span className="block text-xs text-muted-foreground">{rule.legal_reference}</span>
                <span className="block text-xs text-muted-foreground">
                  Rappels {rule.remind_days_before.map((d) => `${String(d)} j`).join(', ')} avant
                </span>
              </span>
              <Switch
                aria-label={`Activer : ${rule.title}`}
                checked={rule.enabled}
                disabled={save.isPending}
                onCheckedChange={(enabled) => {
                  save.mutate(rules.data.map((r) => (r.key === rule.key ? { ...r, enabled } : r)))
                }}
              />
            </li>
          ))}
        </ul>
      )}
      {save.error && (
        <FormError>
          {save.error instanceof ApiError
            ? save.error.message
            : 'Les règles n’ont pas pu être enregistrées.'}
        </FormError>
      )}
    </Frame>
  )
}
